import { MdAutoAwesome, MdChevronLeft, MdChevronRight, MdClose } from "react-icons/md";
import SidebarLinks from "./components/Links";
import logoSvg from "assets/svg/logo1.svg";
import { routes } from "routes";
import { useAuth } from "hooks/useAuth";
import Avatar from "components/avatar";

const ICON_BUTTON =
  "focus-ring flex h-8 w-8 items-center justify-center rounded-control text-fg-muted transition-colors hover:bg-surface-subtle hover:text-fg";

/**
 * Admin sidebar: fixed full-height panel. Below xl it is an off-canvas drawer
 * (`open` + `onClose`, backdrop owned by the admin layout); on xl+ it is always
 * shown and can shrink to an icon rail.
 *
 * @param {object} props
 * @param {boolean} props.open Whether the drawer is shown (always true on xl+).
 * @param {() => void} props.onClose Closes the drawer below xl.
 * @param {boolean} [props.collapsed=false] Icon-only rail; the layout passes false below xl.
 * @param {() => void} props.onToggleCollapse Toggles the xl+ icon rail.
 */
function Sidebar({ open, onClose, collapsed = false, onToggleCollapse }) {
  const { user } = useAuth();
  const upgradeSubject = `${user?.organizationName || "Our Church"} — Account Upgrade`;

  return (
    <aside
      aria-label="Sidebar"
      className={`fixed inset-y-0 left-0 z-50 flex flex-col border-r border-line bg-surface transition-[width,transform] duration-300 ease-in-out xl:z-30 ${
        collapsed ? "w-[76px]" : "w-[260px]"
      } ${open ? "translate-x-0" : "-translate-x-full"}`}
    >
      <div
        className={`flex h-16 shrink-0 items-center ${
          collapsed ? "justify-center px-3" : "justify-between pl-6 pr-3"
        }`}
      >
        <div className="flex items-center gap-2">
          <img className="h-8 w-auto" src={logoSvg} alt={collapsed ? "Atrium" : ""} />
          {!collapsed && <span className="text-xl font-bold text-fg">Atrium</span>}
        </div>
        <button type="button" aria-label="Close sidebar" onClick={onClose} className={`${ICON_BUTTON} xl:hidden`}>
          <MdClose aria-hidden="true" className="h-5 w-5" />
        </button>
        {!collapsed && (
          <button
            type="button"
            aria-label="Collapse sidebar"
            title="Collapse sidebar"
            onClick={onToggleCollapse}
            className={`${ICON_BUTTON} hidden xl:flex`}
          >
            <MdChevronLeft aria-hidden="true" className="h-5 w-5" />
          </button>
        )}
      </div>
      {collapsed && (
        <div className="hidden justify-center pb-2 xl:flex">
          <button
            type="button"
            aria-label="Expand sidebar"
            title="Expand sidebar"
            onClick={onToggleCollapse}
            className={ICON_BUTTON}
          >
            <MdChevronRight aria-hidden="true" className="h-5 w-5" />
          </button>
        </div>
      )}

      <nav aria-label="Main" className="min-h-0 flex-1 overflow-y-auto border-t border-line py-4">
        <SidebarLinks routes={routes} collapsed={collapsed} />
      </nav>

      <div className={`shrink-0 border-t border-line py-4 ${collapsed ? "px-3" : "px-4"}`}>
        {user?.customerStatus === "pathway" && !collapsed && (
          <div className="mb-4 rounded-card bg-primary p-3 text-primary-fg">
            <div className="mb-1.5 flex items-center gap-2">
              <MdAutoAwesome aria-hidden="true" className="h-4 w-4 text-accent" />
              <span className="text-xs font-bold uppercase tracking-wider">Free Tier</span>
            </div>
            <p className="mb-2.5 text-xs leading-relaxed text-primary-fg/80">
              Upgrade to unlock all features — Heartbeat, Groups, Insights, and more.
            </p>
            <a
              href={`mailto:sales@atriumoverview.com?subject=${encodeURIComponent(upgradeSubject)}`}
              className="focus-ring block rounded-control bg-primary-fg/15 py-1.5 text-center text-xs font-semibold transition-colors hover:bg-primary-fg/25"
            >
              Upgrade Account
            </a>
          </div>
        )}

        <div className={`flex items-center ${collapsed ? "justify-center" : "gap-3"}`}>
          <Avatar user={user} size="md" />
          {!collapsed && (
            <div className="min-w-0">
              <p className="truncate text-sm font-semibold text-fg">
                {user?.firstName} {user?.lastName}
              </p>
              <p className="truncate text-xs font-medium capitalize text-fg-muted">{user?.role}</p>
            </div>
          )}
        </div>
      </div>
    </aside>
  );
}

export default Sidebar;
